import { FC, ReactNode, useEffect, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import step1 from '@assets/paymentTutorials/step-1.png';
import step2 from '@assets/paymentTutorials/step-2.png';
import step3 from '@assets/paymentTutorials/step-3.png';
import step4 from '@assets/paymentTutorials/step-4.png';
import step5 from '@assets/paymentTutorials/step-5.png';
import step6 from '@assets/paymentTutorials/step-6.png';
import step7 from '@assets/paymentTutorials/step-7.png';
import step8 from '@assets/paymentTutorials/step-8.png';

// Step text is written to match the real screenshots in
// src/app/assets/paymentTutorials (the Swipesum/NMI key-retrieval journey).
const STEPS: { title: string; body: string; image?: string }[] = [
  {
    title: 'Check your email',
    body: 'After your account is set up, Swipesum (your NMI gateway) sends a "Payment Gateway Account Information" email. Open it to begin.',
    image: step1,
  },
  {
    title: 'Set your password',
    body: 'Click "Set a Password" in that email — the link is valid for 24 hours. Note the username and login URL shown just below it; you\'ll need them next.',
    image: step2,
  },
  {
    title: 'Log in to the gateway',
    body: 'Open your login URL (swipesum.transactiongateway.com/merchants) and sign in. You\'ll land on the gateway dashboard.',
    image: step3,
  },
  {
    title: 'Open Gateway Options',
    body: 'Click "My Settings" at the top, then "Gateway Options". Under "Security Options" you\'ll find "Security Keys".',
    image: step4,
  },
  {
    title: 'Go to Security Keys',
    body: 'Open "Security Keys", then click "Add a New Private Key".',
    image: step5,
  },
  {
    title: 'Create an API key and copy it',
    body: 'Choose key type "API", name it "Zenhammer", and create it. Then click "Copy" next to the key. Keep it private — treat it like a password.',
    image: step6,
  },
  {
    title: 'Paste it in Zenhammer',
    body: 'Back here in Zenhammer, paste the key into the field and click "Connect account".',
    image: step7,
  },
  {
    title: "You're connected",
    body: 'Your payment account now shows as Connected (•••• last 4). You\'re ready to send invoices and get paid.',
    image: step8,
  },
];

// A relevant muted icon per step for the placeholder area, falling back to a
// generic image icon. Purely decorative until real screenshots exist.
const STEP_ICONS: ReactNode[] = [
  <MarkEmailReadRoundedIcon key="0" sx={{ fontSize: 48 }} />,
  <LockResetRoundedIcon key="1" sx={{ fontSize: 48 }} />,
  <LoginRoundedIcon key="2" sx={{ fontSize: 48 }} />,
  <VpnKeyRoundedIcon key="3" sx={{ fontSize: 48 }} />,
  <AddCircleRoundedIcon key="4" sx={{ fontSize: 48 }} />,
  <EditRoundedIcon key="5" sx={{ fontSize: 48 }} />,
  <ContentCopyRoundedIcon key="6" sx={{ fontSize: 48 }} />,
  <CheckCircleRoundedIcon key="7" sx={{ fontSize: 48 }} />,
];

interface NmiKeyTutorialModalProps {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const NmiKeyTutorialModal: FC<NmiKeyTutorialModalProps> = ({
  open,
  onClose,
  onDone,
}) => {
  const [step, setStep] = useState(0);

  // Reset to the first step every time the modal opens.
  useEffect(() => {
    if (open) {
      setStep(0);
    }
  }, [open]);

  const isFirstStep = step === 0;
  const isLastStep = step === STEPS.length - 1;
  const current = STEPS[step];

  const handleNext = () => {
    if (isLastStep) {
      onDone?.();
      onClose();
      return;
    }
    setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="How to find your NMI key"
      size="medium"
      contentClassName="">
      <div className="flex flex-col gap-4">
        {/* Image area (16:9) */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
          {current.image ? (
            <div className="aspect-video w-full">
              <img
                src={current.image}
                alt={current.title}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400">
              {STEP_ICONS[step] ?? <ImageRoundedIcon sx={{ fontSize: 48 }} />}
              <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Screenshot coming soon
              </span>
              <span className="px-6 text-center text-sm text-gray-400">
                {current.title}
              </span>
            </div>
          )}
        </div>

        {/* Title + body */}
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-base font-semibold text-gray-900 dark:text-white">
            {current.title}
          </h3>
          <p className="m-0 text-sm leading-relaxed text-gray-500 dark:text-gray-300">
            {current.body}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, index) => (
              <span
                key={index}
                aria-hidden="true"
                className={
                  index === step
                    ? 'h-2 w-2 rounded-full bg-indigo-600'
                    : 'h-2 w-2 rounded-full bg-gray-300'
                }
              />
            ))}
          </div>
          <span className="text-xs font-medium text-gray-400">
            Step {step + 1} of {STEPS.length}
          </span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-600">
          <Button
            variant="tertiary"
            onClick={handleBack}
            disabled={isFirstStep}
            leftIcon={<ChevronLeftRoundedIcon fontSize="small" />}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            rightIcon={
              isLastStep ? undefined : <ChevronRightRoundedIcon fontSize="small" />
            }>
            {isLastStep ? 'I have my key' : 'Next'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NmiKeyTutorialModal;
